import { AlertCircle, Landmark, Save } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import {
  PayinAmountWithFee,
  type PayinAmountWithFeeState,
} from "@/shared/components/payments/PayinAmountWithFee";
import { quickAmountsFor } from "@/shared/components/payments/payinQuickAmounts";
import useTranslations from "@/shared/lang/useTranslations";

import { usePayinAmountLabels, usePayinRailHint } from "../../hooks/usePayinAmountLabels";
import { PayinRail } from "../../services/paymentsPayin.service";

export interface PayinMexicoFormValues {
  payerName: string;
  /** RFC as typed — optional for SPEI. */
  payerDocument: string;
  /** Gross MXN amount the payer transfers, as the preview resolved it (decimal string). */
  grossAmount: string;
}

export interface PayinMexicoFormProps {
  /** Org the payin is created for — also scopes the fee preview to its effective price book. */
  organizationId: string | null;
  /** Receives validated values; the host page builds the runtime payload and performs the request. */
  onSubmit: (values: PayinMexicoFormValues) => void | Promise<void>;
  isSubmitting: boolean;
}

const buildSchema = (nameRequired: string, nameMax: string) =>
  z.object({
    payerName: z.string().min(1, nameRequired).max(120, nameMax),
    // RFC is genuinely optional on this rail — the backend only demands the name — so an empty string
    // must validate. Length is capped to the DTO's own limit rather than shape-checked: a wrong RFC is
    // the provider's to reject, and refusing a valid-but-unusual one here would block a real collection.
    payerDocument: z.string().max(20).optional(),
  });

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: FormValues = { payerName: "", payerDocument: "" };

/**
 * México creation form for the payments runtime (`POST /internal/payments/payins`, rail SPEI).
 *
 * SPEI has two ways in, and this is the initiated one: the provider mints an **ephemeral receiving CLABE for
 * this payin**, with an expiry, and matches the incoming transfer back to it. The other way — the
 * organization's standing CLABE, credited by push detection — needs no payin and lives in the panel below
 * this form. Both are real; a client that wants a charge tied to one payer and one amount uses this one.
 *
 * Mirrors Brazil: payer identity under React Hook Form, amount in the shared amount+fee control, and the
 * submit sends the preview's resolved gross so the charge matches the numbers on screen. The difference is
 * that only the NAME is required here — the RFC is optional on this rail.
 */
export function PayinMexicoForm({ organizationId, onSubmit, isSubmitting }: PayinMexicoFormProps) {
  const { t } = useTranslations("payins");
  const labels = usePayinAmountLabels();
  const railHint = usePayinRailHint(PayinRail.SPEI);
  const [priced, setPriced] = useState<PayinAmountWithFeeState>({
    mode: "gross",
    preview: null,
    amount: null,
    loading: false,
    error: null,
  });

  const schema = useMemo(
    () => buildSchema(t("validation_payer_name_required"), t("validation_payer_name_max")),
    [t],
  );
  const form = useZodForm(schema, DEFAULTS);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (!priced.preview) return;
      void onSubmit({
        payerName: values.payerName.trim(),
        payerDocument: (values.payerDocument ?? "").trim(),
        grossAmount: priced.preview.gross_amount,
      });
    },
    [priced.preview, onSubmit],
  );

  return (
    <ColocaForm form={form} onSubmit={handleSubmit}>
      <ColocaAlert variant="secondary" icon={Landmark} text={railHint} />

      {!organizationId && <ColocaAlert variant="destructive" icon={AlertCircle} text={t("error_no_org")} />}

      <ColocaInputField
        name="payerName"
        label={t("form_payer_name_label")}
        placeholder={t("form_payer_name_placeholder")}
      />
      <ColocaInputField
        name="payerDocument"
        label={t("form_payer_rfc_label")}
        placeholder={t("form_payer_rfc_placeholder")}
      />

      <PayinAmountWithFee
        organizationId={organizationId}
        rail={PayinRail.SPEI}
        country="MEX"
        currency="MXN"
        labels={labels}
        quickAmounts={quickAmountsFor("MXN")}
        onChange={setPriced}
      />

      <ColocaButton
        type="submit"
        variant="primary"
        icon={Save}
        loading={isSubmitting}
        disabled={!priced.preview || isSubmitting}
        className="w-full"
      >
        {priced.mode === "gross" ? t("submit_gross") : t("submit_net")}
      </ColocaButton>
    </ColocaForm>
  );
}
